import ProgressBar from "./ProgressBar"

import { Box, Card, CardContent, CardHeader, makeStyles, Typography } from "@material-ui/core"
import { ProjectDashboardViewModel } from "../../api/project-dashboard"
import { useFormatSchedule } from "./use-format-schedule"
import { COLOR_ERROR, COLOR_GRAY, COLOR_SECONDARY, OVERDUE_START_COLOR } from "../../../constants"
import { isCompleted, isInprogress, isNotStarted, isRejected } from "../../../utils/status"
import { useTranslations } from "./useTranslations"

const useProjectSchedule = ({ projectDashboard }: ProjectScheduleProps) => {
  const {
    projectCompletion,
    completionPercentageBasedOnTasks,
    scheduleConsumptionPercentage,
    numberOfDaysUntilPlannedStart,
    numberOfDaysFromPlannedStart,
    numberOfDaysProjectStartedLate,
    numberOfDaysUntilPlannedCompletion,
    numberOfDaysFromPlannedCompletion,
    numberOfDaysProjectCompletedLate,
  } = projectDashboard
  const {
    formattedPlannedStart,
    formattedPlannedEnd,
    formattedActualStart,
    formattedActualEnd,
    formattedRejectedDate,
  } = useFormatSchedule(projectDashboard)
  const translations = useTranslations({
    rejectedDate: formattedRejectedDate,
    numberOfDaysUntilPlannedStart,
    numberOfDaysFromPlannedStart,
    numberOfDaysProjectStartedLate,
    numberOfDaysUntilPlannedCompletion,
    numberOfDaysFromPlannedCompletion,
    numberOfDaysProjectCompletedLate,
  })

  const missingPlannedStartDateText = translations.missingPlannedStartDate
  const projectShouldHaveStartedText = translations.shouldHaveStartedDaysAgo
  const projectStartedOnTimeText = translations.projectStartedOnTime
  const projectStartsInTheFutureText = translations.willStartInDays
  const projectStartedLateText = translations.startedDaysLate
  const missingPlannedEndDateText = translations.missingPlannedEndDate
  const projectShouldHaveCompletedText = translations.shouldHaveCompletedDaysAgo
  const projectCompletedOnTimeText = translations.completedOnTime
  const projectCompletesInTheFutureText = translations.shouldCompleteInDays
  const projectCompletedLateText = translations.completedDaysLate

  let startDateLabel = translations.startDate
  let startDateValue = formattedPlannedStart
  let startDateComment = projectStartsInTheFutureText
  let startDateCommentColor = COLOR_SECONDARY

  let endDateLabel = translations.endDate
  let endDateValue = formattedPlannedEnd
  let endDateComment = projectCompletesInTheFutureText
  let endDateCommentColor = COLOR_SECONDARY

  const isNotStartedProject = isNotStarted(projectDashboard.projectStatus)
  if (isNotStartedProject) {
    const shouldHaveStartedAlready = numberOfDaysFromPlannedStart > 0
    if (shouldHaveStartedAlready) {
      startDateComment = projectShouldHaveStartedText
      startDateCommentColor = OVERDUE_START_COLOR
    }

    const shouldHaveAlreadyCompleted = numberOfDaysFromPlannedCompletion > 0
    if (shouldHaveAlreadyCompleted) {
      endDateComment = projectShouldHaveCompletedText
      endDateCommentColor = COLOR_ERROR
    }
  }

  const isInProgressProject = isInprogress(projectDashboard.projectStatus)
  if (isInProgressProject) {
    startDateLabel = translations.startedOn
    startDateValue = formattedActualStart
    const projectStartedLate = numberOfDaysProjectStartedLate > 0
    const projectStartedOnTime = !projectStartedLate
    if (projectStartedLate) {
      startDateComment = projectStartedLateText
      startDateCommentColor = OVERDUE_START_COLOR
    }
    if (projectStartedOnTime) {
      startDateComment = projectStartedOnTimeText
      startDateCommentColor = COLOR_SECONDARY
    }

    const shouldHaveAlreadyCompleted = numberOfDaysFromPlannedCompletion > 0
    const shouldCompleteInTheFuture = numberOfDaysUntilPlannedCompletion > 0
    if (shouldHaveAlreadyCompleted) {
      endDateComment = projectShouldHaveCompletedText
      endDateCommentColor = COLOR_ERROR
    }
    if (shouldCompleteInTheFuture) {
      endDateComment = projectCompletesInTheFutureText
      endDateCommentColor = COLOR_SECONDARY
    }
  }

  const isCompletedProject = isCompleted(projectDashboard.projectStatus)
  if (isCompletedProject) {
    startDateLabel = translations.startedOn
    startDateValue = formattedActualStart
    const projectStartedLate = numberOfDaysProjectStartedLate > 0
    const projectStartedOnTime = !projectStartedLate
    if (projectStartedLate) {
      startDateComment = projectStartedLateText
      startDateCommentColor = OVERDUE_START_COLOR
    }
    if (projectStartedOnTime) {
      startDateComment = projectStartedOnTimeText
      startDateCommentColor = COLOR_SECONDARY
    }

    endDateLabel = translations.completedOn
    endDateValue = formattedActualEnd
    const projectCompletedLate = numberOfDaysProjectCompletedLate > 0
    const projectCompletedOnTime = !projectCompletedLate
    if (projectCompletedLate) {
      endDateComment = projectCompletedLateText
      endDateCommentColor = COLOR_ERROR
    }
    if (projectCompletedOnTime) {
      endDateComment = projectCompletedOnTimeText
      endDateCommentColor = COLOR_SECONDARY
    }
  }

  if (!formattedPlannedStart) {
    startDateComment = missingPlannedStartDateText
    startDateCommentColor = COLOR_GRAY
  }
  if (!formattedPlannedEnd) {
    endDateComment = missingPlannedEndDateText
    endDateCommentColor = COLOR_GRAY
  }

  const isRejectedProject = isRejected(projectDashboard.projectStatus)
  const shouldDisplayRejectedText = isRejectedProject
  const rejectedComment = translations.rejectedComment

  return {
    startDateLabel,
    startDateComment,
    startDateValue,
    startDateCommentColor,
    endDateLabel,
    endDateComment,
    endDateValue,
    endDateCommentColor,
    scheduleConsumptionPercentage,
    projectCompletion,
    completionPercentageBasedOnTasks,
    shouldDisplayRejectedText,
    rejectedComment,
    translations,
  }
}

const ProjectSchedule = ({ projectDashboard }: ProjectScheduleProps) => {
  const classes = useStyles()
  const {
    startDateLabel,
    startDateComment,
    startDateValue,
    startDateCommentColor,
    endDateLabel,
    endDateComment,
    endDateValue,
    endDateCommentColor,
    scheduleConsumptionPercentage,
    projectCompletion,
    completionPercentageBasedOnTasks,
    shouldDisplayRejectedText,
    rejectedComment,
    translations,
  } = useProjectSchedule({ projectDashboard })

  return (
    <Card variant="outlined" className={classes.card}>
      <CardHeader title={translations.scheduleCardHeader} className={classes.cardHeader} disableTypography />
      <CardContent>
        {shouldDisplayRejectedText && (
          <Box sx={{ paddingRight: "16px" }}>
            <Typography variant="body2">
              <span>{rejectedComment}</span>
            </Typography>
          </Box>
        )}
        {!shouldDisplayRejectedText && (
          <>
            <Box sx={{ paddingBottom: "24px" }}>
              <Box sx={{ paddingBottom: "8px" }}>
                <Typography variant="body2">
                  <span>{startDateLabel}</span> {startDateValue}
                </Typography>
                <Box sx={{ color: startDateCommentColor }}>{startDateComment}</Box>
              </Box>
              <Box>
                <Typography variant="body2">
                  <span>{endDateLabel}</span> {endDateValue}
                </Typography>
                <Box sx={{ color: endDateCommentColor }}>{endDateComment}</Box>
              </Box>
            </Box>
            <Box sx={{ paddingBottom: "8px" }}>
              <ProgressBar label={translations.scheduleCompletionLabel} value={scheduleConsumptionPercentage} />
            </Box>
            <Box sx={{ paddingBottom: "8px" }}>
              <ProgressBar label={translations.manuallyUpdatedCompletionLabel} value={projectCompletion} />
            </Box>
            <Box sx={{ paddingBottom: "8px" }}>
              <ProgressBar label={translations.taskBasedCompletionLabel} value={completionPercentageBasedOnTasks} />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
}

const useStyles = makeStyles(() => ({
  card: {
    height: "100%",
  },
  cardHeader: {
    color: COLOR_GRAY,
    fontSize: "1.2rem",
    paddingBottom: 0,
  },

  label: {
    fontWeight: "bold",
  },
}))

export default ProjectSchedule
type ProjectScheduleProps = {
  projectDashboard: ProjectDashboardViewModel
}
